import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Signature } from "@phosphor-icons/react";
import { Section, Field, Toggle, cad } from "@/components/tax/shared";
import { SignaturePad } from "@/components/tax/SignaturePad";

const CONSENT_TEXT = {
  share_with_preparer: "I consent to MyPayAlert securely sharing my tax return, personal information (including my SIN) and supporting documents with the tax preparer selected above, for the sole purpose of reviewing, preparing and filing my return. I can withdraw this consent until the return is filed.",
  accuracy_declaration: "The information and documents I provided are, to the best of my knowledge, correct and complete, and I have disclosed all my income.",
  estimate_understood: "I understand the amounts shown are an ESTIMATE, not a filed or assessed return, and that no refund is guaranteed.",
  t183_understood: "I understand my return will only be filed electronically after my preparer finalizes it in CRA-certified software and I sign Form T183 (Information Return for Electronic Filing) confirming the final amounts.",
  electronic_signature: "I agree that typing my name and drawing my signature below is my electronic signature, with the same effect as a handwritten signature.",
};
const T183_TEXT = {
  reviewed_final_return: "I have reviewed the final return prepared by my preparer, including the refund or balance owing shown above.",
  authorize_efile: "Under Form T183, I authorize the preparer named above (as electronic filer) to file my income tax and benefit return electronically with the Canada Revenue Agency.",
  accuracy_declaration: "The information in my return is correct and complete and fully discloses my income from all sources.",
  electronic_signature: "I agree that typing my name and drawing my signature below is my electronic signature on Form T183.",
};

function T183Explainer({ final, req, preparer }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-700 space-y-2" data-testid="tax-t183-explainer">
      <p className="font-semibold text-slate-900 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-red-600" /> What you are authorizing</p>
      {final ? (
        <>
          <p>CRA requires Form T183 to be signed before an electronic filer sends your return. You are confirming the <strong>final</strong> figures below and authorizing <strong>{preparer?.name}{preparer?.firm ? ` (${preparer.firm})` : ""}</strong> to file it with CRA.</p>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div><p className="text-xs text-slate-500">Final refund</p><p className={`font-bold ${req.final_refund > 0 ? "text-emerald-700" : "text-slate-900"}`} data-testid="tax-t183-refund">{cad(req.final_refund)}</p></div>
            <div><p className="text-xs text-slate-500">Final balance owing</p><p className={`font-bold ${req.final_balance_owing > 0 ? "text-red-600" : "text-slate-900"}`} data-testid="tax-t183-balance">{cad(req.final_balance_owing)}</p></div>
            <div className="col-span-2 text-xs text-slate-500">Prepared with: {req.software}{req.note ? ` · ${req.note}` : ""}</div>
          </div>
        </>
      ) : (
        <p>This step authorizes sharing with a preparer — it is <strong>not</strong> a filing. After review, your preparer will ask you to sign Form T183 for the final return before anything is sent to CRA. Your typed name, signature, the time and your IP address are recorded in a tamper-evident audit trail.</p>
      )}
    </div>
  );
}

export function AuthorizeStep({ ret, meta, onSubmit, submitting }) {
  const final = !!ret.t183_request && !!ret.sharing_consent;
  const texts = final ? T183_TEXT : CONSENT_TEXT;
  const [preparerId, setPreparerId] = useState("");
  const [consents, setConsents] = useState({});
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const all = Object.keys(texts).every((k) => consents[k]);
  const nameOk = name.trim().toLowerCase() === (ret.personal.legal_name || "").trim().toLowerCase() && !!name.trim();
  const ready = all && nameOk && sig && (final || preparerId);
  const preparer = final ? ret.preparer : meta.preparers.find((p) => p.id === preparerId);

  return (
    <div className="space-y-5" data-testid="tax-authorize-step">
      <Section title={final ? "Sign Form T183 — EFILE authorization" : "Consent & submit for tax preparer review"}
        subtitle={final ? "Your preparer finalized your return." : "Choose an approved preparer and authorize secure sharing."}>
        {!final && (
          meta.preparers.length === 0 ? <p className="text-sm text-red-600" data-testid="tax-no-preparers">No approved preparers are available yet. Please check back soon.</p> : (
            <div className="mb-4">
              <label htmlFor="tax-preparer-select" className="text-xs font-semibold text-slate-600 block mb-1.5">Approved tax preparer</label>
              <select id="tax-preparer-select" data-testid="tax-preparer-select" value={preparerId} onChange={(e) => setPreparerId(e.target.value)} className="h-10 w-full md:w-96 rounded-md border border-input px-3 text-sm">
                <option value="">Choose a preparer…</option>
                {meta.preparers.map((p) => <option key={p.id} value={p.id}>{p.name}{p.firm ? ` — ${p.firm}` : ""}</option>)}
              </select>
            </div>
          )
        )}
        <T183Explainer final={final} req={ret.t183_request} preparer={preparer} />
        <div className="mt-4 divide-y divide-slate-100">
          {Object.entries(texts).map(([k, t]) => (
            <Toggle key={k} id={`tax-consent-${k}`} label={t} checked={consents[k]} onChange={(v) => setConsents({ ...consents, [k]: v })} />
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-5 mt-4">
          <div>
            <Field id="tax-sign-name" label={`Type your full legal name exactly: "${ret.personal.legal_name || ""}"`} value={name} onChange={(e) => setName(e.target.value)} />
            {name && !nameOk && <p className="text-xs text-red-600 mt-1" data-testid="tax-sign-name-mismatch">Name must match the legal name on your return.</p>}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1"><Signature className="h-4 w-4" /> Signature</p>
            <SignaturePad onChange={setSig} />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <Button data-testid="tax-authorize-submit-btn" disabled={!ready || submitting} className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8"
            onClick={() => onSubmit({ preparer_id: final ? undefined : preparerId, legal_name: name, consents, signature_data_url: sig })}>
            {submitting ? "Signing securely…" : final ? "Sign T183" : "Sign & submit for preparer review"}
          </Button>
        </div>
      </Section>
    </div>
  );
}
